import React, { useMemo } from 'react'
import { trans } from 'sharedKernel/i18n'
import { ReservationActionMenu } from './ReservationActionMenu'
import { IMeetingRoomReservationDetail, IMeetingRoomReservationRow } from './types'
import {
  EmptyTableMessage,
  RequesterLink,
  ReservationsTable,
  ReservationsTableScroll,
  ReservationsTableWrapper,
  StatusBadge,
} from './styles'

interface MeetingRoomReservationsTableProps {
  rows: IMeetingRoomReservationDetail[]
  searchTerm?: string
  onViewDetails?: (row: IMeetingRoomReservationDetail) => void
  onEdit?: (row: IMeetingRoomReservationRow) => void
  onApprove?: (row: IMeetingRoomReservationRow) => void
  onReject?: (row: IMeetingRoomReservationRow) => void
  onCancel?: (row: IMeetingRoomReservationRow) => void
}

const filterReservations = (
  rows: IMeetingRoomReservationDetail[],
  searchTerm: string
): IMeetingRoomReservationDetail[] => {
  const term = searchTerm.trim().toLowerCase()
  if (!term) return rows

  return rows.filter(row => {
    const haystack = [
      row.requesterName,
      row.reservationDate,
      row.timeRange,
      row.requesterType,
      row.requestDate,
      row.status,
    ]
      .join(' ')
      .toLowerCase()
    return haystack.includes(term)
  })
}

const getStatusLabel = (status: IMeetingRoomReservationRow['status']): string => {
  switch (status) {
    case 'pendente':
      return trans('meetingRoom.reservations.status.pending')
    case 'aprovada':
      return trans('meetingRoom.reservations.status.approved')
    case 'reprovada':
      return trans('meetingRoom.reservations.status.rejected')
    case 'cancelada':
      return trans('meetingRoom.reservations.status.cancelled')
    default:
      return status
  }
}

const getRequesterTypeLabel = (type: IMeetingRoomReservationRow['requesterType']): string => {
  switch (type) {
    case 'externo':
      return trans('meetingRoom.reservations.requesterType.external')
    case 'residente':
      return trans('meetingRoom.reservations.requesterType.resident')
    default:
      return type
  }
}

export const MeetingRoomReservationsTable = ({
  rows,
  searchTerm = '',
  onViewDetails,
  onEdit,
  onApprove,
  onReject,
  onCancel,
}: MeetingRoomReservationsTableProps): JSX.Element => {
  const filteredRows = useMemo(
    () => filterReservations(rows, searchTerm),
    [rows, searchTerm]
  )

  return (
    <ReservationsTableWrapper>
      <ReservationsTableScroll>
        <ReservationsTable responsive>
          <thead>
            <tr>
              <th>{trans('meetingRoom.reservations.column.requesterName')}</th>
              <th>{trans('meetingRoom.reservations.column.reservationDate')}</th>
              <th>{trans('meetingRoom.reservations.column.time')}</th>
              <th>{trans('meetingRoom.reservations.column.requesterType')}</th>
              <th>{trans('meetingRoom.reservations.column.requestDate')}</th>
              <th>{trans('meetingRoom.reservations.column.status')}</th>
              <th>{trans('meetingRoom.reservations.column.action')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <EmptyTableMessage>
                    {trans('meetingRoom.reservations.empty')}
                  </EmptyTableMessage>
                </td>
              </tr>
            ) : (
              filteredRows.map(row => (
                <tr key={row.id}>
                  <td>
                    <RequesterLink
                      type="button"
                      onClick={() => onViewDetails?.(row)}
                    >
                      {row.requesterName}
                    </RequesterLink>
                  </td>
                  <td>{row.reservationDate}</td>
                  <td>{row.timeRange}</td>
                  <td>{getRequesterTypeLabel(row.requesterType)}</td>
                  <td>{row.requestDate}</td>
                  <td>
                    <StatusBadge $status={row.status}>
                      {getStatusLabel(row.status)}
                    </StatusBadge>
                  </td>
                  <td>
                    <ReservationActionMenu
                      row={row}
                      onViewDetails={onViewDetails}
                      onEdit={onEdit}
                      onApprove={onApprove}
                      onReject={onReject}
                      onCancel={onCancel}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </ReservationsTable>
      </ReservationsTableScroll>
    </ReservationsTableWrapper>
  )
}
